import $ from 'jquery';
import { invoke } from '../../../shared/modules/common';
import { render } from 'mustache';
import view from './localNavigationView';
import { select } from './localNavigation';

/**
 * The Action factory. Contains a factory method to return an Action instance.
 */
var ActionFactory = {
    /**
     * Create an Action object.
     * @param {object} item The item which the action is associated with.
     * @param {object} actionConfig The action configuration object (it can be an object or a function).
     * @return An Action instance typed according to the input config.
     */
    create: function (item, actionConfig) {
        var createdAction;
        if (item && actionConfig) {
            if ($.isFunction(actionConfig)) {
                createdAction = new ActionFunction(item, actionConfig);
            } else {
                createdAction = new ActionUrl(item, actionConfig);
            }
        }
        return createdAction;
    }
};

function Action(item) {
    this.item = item;
    this.enabled = !item.disabled;
}

$.extend(Action.prototype, {
    setEnabled: function (enabled) {
        this.enabled = enabled;
    }
});

/**
 * Action object that represents a function call bound to an item. It follows the Command pattern.
 * @param {object} item The visible item (Node, PageGroup) which the action is associated to.
 * @param {function} fn The function that should be called when clicking on the visible item.
 */
function ActionFunction(item, fn) {
    Action.call(this, item);
    this.fn = fn;
}

$.extend(ActionFunction.prototype, Action.prototype, {
    render: function ($element) {
        var self = this;
        $element.on('click', function () {
            if (self.enabled) {
                select(self.item.id, function () {
                    self.execute();
                });
            }
        });
    },

    execute: function () {
        invoke(this.fn, this.item.id);
    }
});

/**
 * Action object that represents an URL redirect bound to an item.
 * @param {object} item The visible item (Node, PageGroup) which the action is associated to.
 * @param {function} actionConfig The action config object defined by the client app. It can be an object or a
 *     string. The object properties (url, frame) will be used to generate an <a> tag.
 */
function ActionUrl(item, actionConfig) {
    Action.call(this, item, actionConfig);
    this.onclick = ' uwr.localnav.select("' + this.item.id + '");';
    if (typeof actionConfig === 'object' && actionConfig.url) {
        this.href = actionConfig.url;
        this.target = actionConfig.frame;
    }
}

$.extend(ActionUrl.prototype, Action.prototype, {
    render: function ($element) {
        this.$element = $element;
        this.setAttributes();
    },

    setEnabled: function (enabled) {
        Action.prototype.setEnabled.call(this, enabled);
        if (enabled) {
            this.setAttributes();
        } else {
            this.$element.removeAttr('onclick');
            this.$element.removeAttr('href');
            this.$element.removeAttr('target');
        }
    },

    setAttributes: function () {
        if (this.enabled && this.href !== undefined) {
            this.$element.attr('href', this.href);
            this.$element.attr('onclick', this.onclick);
            if (this.target !== undefined) {
                this.$element.attr('target', this.target);
            }
        }
    }
});

/**
 * Abstract item. An item may have zero or more child items.
 * @param {object} config The configuration of this item.
 *
 * Concrete child classes must implement getView() and may override these two methods:
 * - this.render: This implementation creates the DOM element and appends it into the parent node
 *   (the function's only argument).
 * - this.getItemsContainer: A function that returns the DOM element containing the child items.
 *
 * Configuration parameters:
 * - parentObject: the parent item of this one
 * - rootObject: the Local Navigation object the item belongs to
 * - id: the unique id of this item
 * - active: if this item is active (selected) or not
 * - disabled: if this item is disabled or not (on a disabled item actions are not called)
 * - action: the action configuration
 */
function AbstractItem(config) {
    this.$domElement = null; // Will be set later
    this.parentObject = config.parentObject;
    this.rootObject = config.rootObject;
    this.id = config.id;
    this.active = config.active || false;
    this.disabled = config.disabled || false;
    this.action = ActionFactory.create(this, config.action);
    this.items = [];
}

$.extend(AbstractItem.prototype, {
    /**
     * Returns the container element the children should be rendered in.
     */
    getItemsContainer: function () {
        return this.$domElement;
    },

    /**
     * This is a function that returns a view for rendering. Concrete Item classes must implement it.
     */
    getView: null,

    getParent: function () {
        return this.parentObject;
    },

    createSubItems: function (itemConfigs, ItemClass) {
        var itemConfig,
            i;
        if (itemConfigs && itemConfigs.length > 0) {
            for (i = 0; i < itemConfigs.length; i++) {
                // Clone it, not to temper with the original one
                itemConfig = $.extend(true, {}, itemConfigs[i]);
                itemConfig.parentObject = this;
                itemConfig.rootObject = this.rootObject || this.parentObject;
                this.items.push(new ItemClass(itemConfig));
            }
        }
    },

    /**
     * @param {object} $parent The jQuery DOM object the rendered HTML code is appended into.
     */
    render: function ($parent) {
        var $containerElem,
            i;

        if (!this.$domElement) {
            this.$domElement = $(render(this.getView(), this));
            if (this.items.length > 0) {
                $containerElem = this.getItemsContainer();
                for (i = 0; i < this.items.length; i++) {
                    this.items[i].render($containerElem);
                }
            }
            if (this.action) {
                this.action.render($('> a', this.$domElement));
            }
            $parent.append(this.$domElement);
        }

        return this.$domElement;
    },

    /**
     * Activates the item or one of its child items. The id must be passed, even if the current item needs
     * to be activated.
     * @param {string} id The item's id.
     * @param {boolean} active True/false means to activate/inactivate the item.
     */
    setActive: function (id, active) {
        var isSameId = this.id && this.id === id,
            shouldBeActive = (active !== undefined ? active : true) && isSameId,
            i;
        this.active = shouldBeActive;
        if (shouldBeActive) {
            this.$domElement.addClass('uwr-local-nav-active');
        } else {
            this.$domElement.removeClass('uwr-local-nav-active');
            for (i = 0; i < this.items.length; i++) {
                this.items[i].setActive(id);
            }
        }
    },

    /**
     * Enables the item or one of its child items. The id must be passed, even if the current item needs
     * to be activated.
     * @param {boolean} enabled True/false means to enable/disable the item.
     */
    setEnabled: function (enabled) {
        var shouldBeEnabled = enabled !== undefined ? enabled : true;
        if (shouldBeEnabled) {
            this.$domElement.removeClass('uwr-local-nav-disabled');
        } else {
            this.$domElement.addClass('uwr-local-nav-disabled');
        }
        this.action.setEnabled(shouldBeEnabled);
    }
});

/**
 * The IconPanelItem is an item of the Icon Panel, which can have an Action added, and clicking it selects the
 * active Page Group associated to this item.
 * @param {object} config The configuration of this item.
 *
 * Configuration parameters (see AbstractItem for more as ancestor object):
 * - label: The displayed name of this item
 * - iconClass: the CSS class name associated to this item, which will be displayed
 */
export function IconPanelItem(config) {
    AbstractItem.call(this, config);
    this.label = config.label || config.id;
    this.iconClass = config.iconClass;
}

$.extend(IconPanelItem.prototype, AbstractItem.prototype, {
    getView: function () {
        return view.iconPanelItem;
    },

    render: function ($parent) {
        var $iconPanelItem = AbstractItem.prototype.render.call(this, $parent),
            self = this;
        $iconPanelItem.on('click', function () {
            self.rootObject.show(self.id);
        });
    }
});

/**
 * The IconPanel is the left side main container of the Local Navigation. It contains icons which represent
 * Page Groups.
 * @param {object} config The configuration of this item.
 *
 * Configuration parameters (see AbstractItem for the base params):
 * - pageGroups: The set of the PageGroup items, which define the panel items.
 */
export function IconPanel(config) {
    AbstractItem.call(this, config);
    this.createSubItems(config.pageGroups, IconPanelItem);
}

$.extend(IconPanel.prototype, AbstractItem.prototype, {
    getItemsContainer: function () {
        return $('ul', this.$domElement);
    },

    getView: function () {
        return view.iconPanel;
    }
});

/**
 * Node is a menu item of the Page Group and is placed in a Panel. It can have an Action which is activated
 * on click.
 * @param {object} config The configuration of this item.
 *
 * Configuration parameters: same as AbstractItem's config
 */
export function Node(config) {
    AbstractItem.call(this, config);
    this.rootObject.itemCache.add(this.id, this);
    this.label = config.label || config.id;
    this.hasChildren = config.nodes && config.nodes.length > 0;
    this.level = 0;
    this.createSubItems(config.nodes, Node);
}

$.extend(Node.prototype, AbstractItem.prototype, {
    /**
     * Returns the Node with the given id or undefined if no such item exists.
     * @param {string} id The id of the Node.
     */
    get: function (id) {
        this.rootObject.itemCache.get(id, Node);
    },

    getItemsContainer: function () {
        return $('ul', this.$domElement);
    },

    getView: function () {
        return view.pageGroupPanelNode;
    },

    isCollapsible: function () {
        return (this.parentObject && $.isFunction(this.parentObject.isCollapsible))
            ? this.parentObject.isCollapsible()
            : true;
    },

    expandOrCollapse: function () {
        if (this.hasChildren) {
            if (this.isCollapsible()) {
                if (this.$domElement.hasClass('uwr-local-nav-collapsed')) {
                    this.$domElement.removeClass('uwr-local-nav-collapsed');
                } else {
                    this.$domElement.addClass('uwr-local-nav-collapsed');
                }
            }
        }
    },

    expandParents: function (id) {
        if (this.id !== id) {
            this.$domElement.removeClass('uwr-local-nav-collapsed');
        }
        if (this.parentObject && $.isFunction(this.parentObject.expandParents)) {
            this.parentObject.expandParents();
        }
    },

    render: function ($parent) {
        var self = this,
            $node,
            leftPadding = this.hasChildren ? 10 : 15;
        if (this.hasChildren) {
            this.action = ActionFactory.create(this, function () {
                self.expandOrCollapse();
            });
        }
        $node = AbstractItem.prototype.render.call(this, $parent);
        leftPadding += this.level * 10;
        $('> a', $node).css('padding-left', leftPadding + 'px');
    },

    setActive: function (id, active) {
        if (this.id === id) {
            if (this.hasChildren) {
                active = false;
            }
            this.expandParents(id);
        }
        AbstractItem.prototype.setActive.call(this, id, active);
    },

    setLevel: function (level) {
        var i;
        this.level = level;
        if (this.items) {
            for (i = 0; i < this.items.length; i++) {
                this.items[i].setLevel(this.level + 1);
            }
        }
    }
});

/**
 * Panel is a collection of menu items (Nodes) and could have an own title.
 * @param {object} config The configuration of this item.
 *
 * Configuration parameters (see AbstractItem for the base params):
 * - label: The displayed title of this panel.
 * - nonCollapsible: If the items of this panel are collapsible or not. It have effect only if some of the panel
 *   items are not leaf nodes.
 */
export function Panel(config) {
    var i;
    AbstractItem.call(this, config);
    this.createSubItems(config.nodes, Node);
    this.rootObject.itemCache.add(this.id, this);
    this.collapsible = config.nonCollapsible !== undefined ? !config.nonCollapsible : true;
    this.label = config.label;

    if (this.items) {
        for (i = 0; i < this.items.length; i++) {
            this.items[i].setLevel(0);
        }
    }
}

$.extend(Panel.prototype, AbstractItem.prototype, {
    getItemsContainer: function () {
        return $('ul', this.$domElement);
    },

    getView: function () {
        return view.pageGroupPanel;
    },

    isCollapsible: function () {
        return this.collapsible;
    },

    render: function ($parent) {
        AbstractItem.prototype.render.call(this, $('.uwr-local-nav-panel-container', $parent));
    }
});

/**
 * Page Group is the right main panel of the Local Navigation. It contains all menu items, organized in Panels.
 * @param {object} config The configuration of this item.
 *
 * Configuration parameters (see AbstractItem for the base params):
 * - label: The displayed name of this item
 * - iconClass: The CSS class name associated to this item, which will be displayed
 */
export function PageGroup(config) {
    AbstractItem.call(this, config);
    this.createSubItems(config.panels, Panel);
    this.rootObject.itemCache.add(this.id, this);
    this.action = undefined;
    this.iconClass = config.iconClass;
    this.label = config.label || 'Label:' + this.id;
    this.disabled = config.disabled || false;
}

$.extend(PageGroup.prototype, AbstractItem.prototype, {
    show: function () {
        this.$domElement.removeClass('uwr-local-nav-hidden');
    },

    hide: function () {
        this.$domElement.addClass('uwr-local-nav-hidden');
    },

    /**
     * Returns the Page Group with the given id or undefined if no such item exists.
     * @param {string} id The id of the Page Group.
     */
    get: function (id) {
        this.rootObject.itemCache.get(id, PageGroup);
    },

    getView: function () {
        return view.pageGroup;
    }
});



//////////////////
// WEBPACK FOOTER
// ./widgets/LocalNavigation/js/localNavigationItems.js
// module id = 92
// module chunks = main